/**
 * Copyright © 2026 OwnersTable Inc. All rights reserved.
 * This source code is proprietary and confidential.
 * Unauthorized copying or distribution is strictly prohibited.
 */

import React, { useState } from "react";
import BookmarkBorderIcon from "@mui/icons-material/BookmarkBorder";
import ListAltIcon from "@mui/icons-material/ListAlt";
import { StyledButton } from "../reset_button/styles";
import { useNotificationContext } from "../../contexts/notificationContext";
import { SaveQueryDialog } from "./SaveQueryDialog";
import { SavedQueriesDialog } from "./SavedQueriesDialog";
import { hasChangedFromDefaults, isLoadedQueryModified, toComparableFilters, toLoadedQuery } from "./useSavedQueryState";

export const restrictedQueryMessage = (name) =>
  `Some filters in '${name}' are no longer available to you and were removed. ` +
  "Please review the filters before running the report.";

/**
 * Save Query / Update Query and View Queries for a report's filter bar.
 * The page owns the filters: `onApply(filters, labelOptions, { restricted })` fills them and runs the report,
 * unless some saved filters were removed, when it waits for Update.
 * `adapter.deserialize(filters, version, record)` may return `notices`, shown as a warning on Apply.
 */
export const SavedQueryControls = ({
  reportType,
  contextId,
  filters,
  lookups,
  adapter,
  state,
  onApply,
  appliedFilters,
  disabled = false,
  isDataLoaded = true,
}) => {
  const showNotification = useNotificationContext();
  const [saveOpen, setSaveOpen] = useState(false);
  const [listOpen, setListOpen] = useState(false);
  const { loadedQuery, setLoadedQuery, setLoadedFilters, clearLoadedQuery } = state;

  const serialized = adapter.serialize(filters, lookups);
  const savedFilters = contextId ? { ...serialized, context_id: contextId } : serialized;
  // Only what the report last ran can be saved: filters changed without Update must be run first.
  const isApplied =
    appliedFilters === undefined || toComparableFilters(adapter, filters) === toComparableFilters(adapter, appliedFilters);
  // Saving also needs the report's table loaded; a new query must differ from the defaults, a loaded one can be renamed as is.
  const canSave = isApplied && isDataLoaded && (Boolean(loadedQuery) || hasChangedFromDefaults(adapter, serialized));

  const handleApply = (record) => {
    const {
      filters: restored,
      labelOptions,
      notices = [],
    } = adapter.deserialize(record.filters, record.filters_version, record);
    onApply(restored, labelOptions, { restricted: Boolean(record.filters_restricted) });
    setLoadedFilters(toComparableFilters(adapter, restored));
    setLoadedQuery(toLoadedQuery(record));
    setListOpen(false);
    // One snackbar shows at a time, so every warning goes in one message.
    const warnings = [...(record.filters_restricted ? [restrictedQueryMessage(record.name)] : []), ...notices];
    if (warnings.length) showNotification(warnings.join(" "), "warning");
  };

  const handleDeleted = (record) => {
    if (loadedQuery?.id === record.id) clearLoadedQuery();
  };

  const handleSaved = (record) => {
    setLoadedFilters(toComparableFilters(adapter, filters));
    setLoadedQuery(record);
    setSaveOpen(false);
  };

  return (
    <React.Fragment>
      <StyledButton
        variant="outlined"
        startIcon={<BookmarkBorderIcon />}
        onClick={() => setSaveOpen(true)}
        disabled={disabled || !canSave}
        data-testid="saved-query-save-btn"
      >
        {loadedQuery ? "Update Query" : "Save Query"}
      </StyledButton>
      <StyledButton
        variant="outlined"
        startIcon={<ListAltIcon />}
        onClick={() => setListOpen(true)}
        disabled={disabled}
        data-testid="saved-query-view-btn"
      >
        View Queries
      </StyledButton>

      <SaveQueryDialog
        open={saveOpen}
        onClose={() => setSaveOpen(false)}
        reportType={reportType}
        savedFilters={savedFilters}
        filtersVersion={adapter.version}
        filtersChanged={isLoadedQueryModified(adapter, filters, state)}
        loadedQuery={loadedQuery}
        onSaved={handleSaved}
      />
      <SavedQueriesDialog
        open={listOpen}
        onClose={() => setListOpen(false)}
        reportType={reportType}
        contextId={contextId}
        onApply={handleApply}
        onDeleted={handleDeleted}
      />
    </React.Fragment>
  );
};
